// Copyright © 2019-2026 Focal Systems. All rights reserved.

import {
  type CellData,
  type ColumnDef,
  type ColumnVisibilityState,
  columnSizingFeature,
  columnVisibilityFeature,
  flexRender,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  rowPaginationFeature,
  rowSortingFeature,
  type SortingState,
  type TableFeatures,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// v9 requires features to be registered explicitly. We drive sorting and
// pagination server-side (manual mode); column visibility and column sizing
// (the `size`/`forceWidth` column widths) run on the client.
const dataTableFeatures = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
  columnVisibilityFeature,
  columnSizingFeature,
});
type DataTableFeatures = typeof dataTableFeatures;

/**
 * `ColumnDef` bound to the DataTable's registered feature set. Consumers use
 * this instead of the raw v9 `ColumnDef` so they don't have to thread the
 * `TFeatures` generic through every column definition.
 */
export type DataTableColumnDef<TData extends RowData> = ColumnDef<DataTableFeatures, TData, unknown>;

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TFeatures extends TableFeatures, TData extends RowData, TValue extends CellData = CellData> {
    className?: string;
    forceWidth?: boolean;
    /** Hide this column by default. User can re-enable it via the columns toggle. */
    defaultHidden?: boolean;
    /** Human-readable label shown in the columns toggle. Falls back to `header` if a string. */
    columnLabel?: string;
    /** Pin the column to the left or right edge during horizontal scroll. */
    sticky?: "left" | "right";
  }
}

import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  X,
} from "lucide-react";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export interface DataTableProps<TData extends RowData> {
  columns: DataTableColumnDef<TData>[];
  data: TData[];
  totalCount: number;
  pageSize?: number;
  isLoading?: boolean;
  // Server-side pagination
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  // Server-side sorting
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  // Search
  search?: string;
  onSearchChange?: (search: string) => void;
  searchPlaceholder?: string;
  // Search debounce (ms). 0 = submit on Enter only.
  searchDebounceMs?: number;
  // Filter presets (rendered in the toolbar group, before `toolbar`)
  presets?: React.ReactNode;
  // Filters (rendered after search, left-aligned)
  filters?: React.ReactNode;
  // Toolbar (rendered on the right)
  toolbar?: React.ReactNode;
  // Refresh callback
  onRefresh?: () => void;
  isRefreshing?: boolean;
  // Export callback – when provided, shows an Export button in the footer
  onExport?: () => Promise<void>;
  // Optional note rendered after the count in the footer
  footerNote?: React.ReactNode;
  // Column visibility — when `storageKey` is set, the toggle dropdown is shown and the chosen
  // state is persisted to localStorage under `data-table:${storageKey}:columns`.
  storageKey?: string;
  // Whether the footer offers paging. Set false for a table whose consumer loads the whole
  // result set at once and filters it client-side — the page-size select and the page nav
  // would then be controls over a single page, which is worse than no controls at all. The
  // `pagination` prop is still required: the table's own state needs somewhere to live.
  paginated?: boolean;
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  totalCount,
  isLoading,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchDebounceMs = 0,
  presets,
  filters,
  toolbar,
  onExport,
  onRefresh,
  isRefreshing,
  footerNote,
  storageKey,
  paginated = true,
}: DataTableProps<TData>) {
  const { t } = useTranslation("back-office");
  const [searchValue, setSearchValue] = useState(search ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);

  // Column visibility: defaults from columnDef.meta.defaultHidden, with overrides persisted to
  // localStorage when storageKey is provided.
  const localStorageKey = storageKey ? `data-table:${storageKey}:columns` : null;
  const defaultVisibility = useMemo<ColumnVisibilityState>(() => {
    const fromDefaults: ColumnVisibilityState = {};
    for (const col of columns) {
      const id = ((col as { id?: string }).id ?? (col as { accessorKey?: string }).accessorKey) as string | undefined;
      if (!id) continue;
      if (col.meta?.defaultHidden) fromDefaults[id] = false;
    }
    return fromDefaults;
  }, [columns]);
  // Read persisted visibility once, keyed on localStorageKey only — defaultVisibility is the seed, not a trigger for re-reading localStorage.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional one-time read of persisted state
  const initialVisibility = useMemo<ColumnVisibilityState>(() => {
    if (localStorageKey && typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(localStorageKey);
        if (stored) return { ...defaultVisibility, ...(JSON.parse(stored) as ColumnVisibilityState) };
      } catch {
        // ignore malformed stored state
      }
    }
    return defaultVisibility;
  }, [localStorageKey]);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(initialVisibility);

  useEffect(
    function persistColumnVisibility() {
      if (!localStorageKey || typeof window === "undefined") return;
      try {
        localStorage.setItem(localStorageKey, JSON.stringify(columnVisibility));
      } catch {
        // localStorage may be unavailable (private mode, quota)
      }
    },
    [localStorageKey, columnVisibility],
  );

  // Sync internal value when the parent resets search externally (e.g. "Clear filters")
  useEffect(
    function syncSearchFromParent() {
      setSearchValue(search ?? "");
    },
    [search],
  );

  // Submitting a search always resets to the first page — the result set changes, so the
  // current page index is meaningless (matches how filter changes reset the page in callers).
  const submitSearch = useCallback(
    (value: string) => {
      onSearchChange?.(value);
      onPaginationChange((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    [onSearchChange, onPaginationChange],
  );

  const flushSearch = useCallback(
    (value: string) => {
      clearTimeout(debounceRef.current);
      submitSearch(value);
    },
    [submitSearch],
  );

  function handleSearchInput(value: string) {
    setSearchValue(value);
    if (searchDebounceMs > 0) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => submitSearch(value), searchDebounceMs);
    }
  }

  useEffect(function cleanupSearchDebounceTimer() {
    return () => clearTimeout(debounceRef.current);
  }, []);

  // Server-side queries briefly report `totalCount` 0 while data loads (no
  // consumer keeps previous data across changes). Retain the last known total so
  // paging between pages of the same result set doesn't collapse the count to
  // "/1" mid-load. The fallback applies ONLY while a non-first page is loading —
  // the one case where a 0 is a transient artifact of paging within a known
  // result set. In every other case 0 is the truth and we show it: a new
  // search/filter (which resets to page 0) shows a skeleton while loading, and a
  // settled query that genuinely returned no rows shows 0 rather than the stale
  // total from before the search.
  const lastKnownTotalRef = useRef(totalCount);
  if (totalCount > 0) lastKnownTotalRef.current = totalCount;
  const isPagingWithinDataset = isLoading && pagination.pageIndex > 0;
  const displayTotal = totalCount > 0 ? totalCount : isPagingWithinDataset ? lastKnownTotalRef.current : 0;
  // Unpaginated tables hold the whole result set on the one page they have.
  const pageCount = paginated ? Math.ceil(displayTotal / pagination.pageSize) : 1;

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns,
    pageCount,
    state: { sorting, pagination, columnVisibility },
    onSortingChange,
    onPaginationChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    manualSorting: true,
    // Lists are always server-sorted with a default column, so there is no real "unsorted" state —
    // clicking a sorted column cycles asc↔desc instead of clearing to none (which would show no
    // arrow while the data is still sorted by the default, misrepresenting the actual order).
    enableSortingRemoval: false,
  });

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    flushSearch(searchValue);
  }

  // Column visibility switcher — lives in the actions column header when one exists, otherwise it's
  // rendered in a dedicated sticky trailing header cell so it's always reachable.
  const columnToggle = storageKey ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Settings2 className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap max-h-96 overflow-y-auto">
        {table
          .getAllLeafColumns()
          .filter((col) => col.getCanHide())
          .map((col) => {
            const label =
              col.columnDef.meta?.columnLabel ??
              (typeof col.columnDef.header === "string" ? col.columnDef.header : col.id);
            return (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={col.getIsVisible()}
                onCheckedChange={(value) => col.toggleVisibility(value)}
                onSelect={(e) => e.preventDefault()}
              >
                {label}
              </DropdownMenuCheckboxItem>
            );
          })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setColumnVisibility(defaultVisibility)}>
          <RotateCcw className="h-4 w-4" />
          {t("dataTable.resetColumns")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  // When there's no actions column to host the switcher, append a trailing column for it.
  const hasActionsColumn = table.getAllLeafColumns().some((col) => col.id === "actions");
  const showTrailingToggle = !!storageKey && !hasActionsColumn;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      {(onSearchChange || presets || filters || toolbar) && (
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            {onSearchChange && (
              <form onSubmit={handleSearchSubmit} className="w-56">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        if (searchValue) {
                          setSearchValue("");
                          flushSearch("");
                        }
                      }
                    }}
                    className={`w-full pl-9 ${searchValue ? "pr-8" : ""}`}
                  />
                  {searchValue && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchValue("");
                        flushSearch("");
                        inputRef.current?.focus();
                      }}
                      className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </form>
            )}
            {filters}
          </div>
          {(presets || toolbar) && (
            <div className="flex items-center gap-2 shrink-0 self-start">
              {presets}
              {toolbar}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-sidebar hover:bg-sidebar">
                {headerGroup.headers.map((header, i) => {
                  const sticky = header.column.columnDef.meta?.sticky;
                  const prevSticky = i > 0 ? headerGroup.headers[i - 1]?.column.columnDef.meta?.sticky : undefined;
                  return (
                    <TableHead
                      key={header.id}
                      className={[
                        header.column.columnDef.meta?.className,
                        i === 0 ? "rounded-tl-md" : "",
                        i === headerGroup.headers.length - 1 && !showTrailingToggle ? "rounded-tr-md" : "",
                        sticky === "left"
                          ? "sticky left-0 z-10 bg-sidebar shadow-[inset_-1px_0_0_var(--color-border)]"
                          : "",
                        sticky === "right"
                          ? "sticky right-0 z-10 bg-sidebar border-l-0 shadow-[inset_1px_0_0_var(--color-border)]"
                          : "",
                        prevSticky === "left" ? "border-l-0" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={
                        header.column.columnDef.meta?.forceWidth
                          ? { width: header.getSize(), minWidth: header.getSize(), maxWidth: header.getSize() }
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : storageKey && header.column.id === "actions" ? (
                        columnToggle
                      ) : header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5" />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <ArrowDown className="h-3.5 w-3.5" />
                          ) : (
                            <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
                {showTrailingToggle && (
                  <TableHead className="w-12 rounded-tr-md sticky right-0 z-10 bg-sidebar border-l-0 shadow-[inset_1px_0_0_var(--color-border)]">
                    {columnToggle}
                  </TableHead>
                )}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length + (showTrailingToggle ? 1 : 0)}
                  className="py-16 text-center"
                >
                  <InlineSpinner className="py-0" />
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length + (showTrailingToggle ? 1 : 0)}
                  className="py-16 text-center text-muted-foreground"
                >
                  {t("dataTable.noResults")}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="group h-12 hover:bg-muted!">
                  {row.getVisibleCells().map((cell) => {
                    const sticky = cell.column.columnDef.meta?.sticky;
                    return (
                      <TableCell
                        key={cell.id}
                        className={[
                          cell.column.columnDef.meta?.className,
                          sticky === "left" ? "sticky left-0 z-10 bg-background group-hover:bg-muted" : "",
                          sticky === "right" ? "sticky right-0 z-10 bg-background group-hover:bg-muted" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={
                          cell.column.columnDef.meta?.forceWidth
                            ? {
                                width: cell.column.getSize(),
                                minWidth: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                              }
                            : undefined
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                  {showTrailingToggle && (
                    <TableCell className="w-12 sticky right-0 z-10 bg-background group-hover:bg-muted" />
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={onRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            </Button>
          )}
          <p className="text-sm text-muted-foreground">
            {displayTotal > 0
              ? !paginated
                ? t("dataTable.rows", { count: displayTotal })
                : t("dataTable.showing", {
                    from: pagination.pageIndex * pagination.pageSize + 1,
                    to: Math.min((pagination.pageIndex + 1) * pagination.pageSize, displayTotal),
                    total: displayTotal,
                  })
              : isLoading
                ? t("common.loading")
                : t("dataTable.noResultsCount")}
          </p>
          {footerNote && <span className="text-sm text-muted-foreground ml-4">{footerNote}</span>}
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              disabled={exporting || totalCount === 0}
              onClick={async () => {
                setExporting(true);
                try {
                  await onExport();
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t("dataTable.export")}
            </Button>
          )}
          {paginated && (
            <>
              <Select
                value={String(pagination.pageSize)}
                onValueChange={(value) =>
                  onPaginationChange({
                    pageIndex: 0,
                    pageSize: Number(value),
                  })
                }
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {t("dataTable.rows", { count: size })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm tabular-nums px-2 inline-flex items-center gap-1.5">
                  {pageCount > 0 || !isLoading ? (
                    `${pagination.pageIndex + 1} / ${pageCount || 1}`
                  ) : (
                    <>
                      <Skeleton className="h-4 w-5" />/<Skeleton className="h-4 w-5" />
                    </>
                  )}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => table.setPageIndex(pageCount - 1)}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
